import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FileText, Calendar, CheckCircle, ExternalLink, ArrowLeft, AlertTriangle } from 'lucide-react';
import { getMsqCaseStudyById } from '../utils/msqCaseStudiesApi';
import { msqCaseStudyStatuses } from '../utils/mockMsqCaseStudyData';

const CaseStudy = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [caseStudy, setCaseStudy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCaseStudy = async () => {
      if (!id) {
        setError('No case study ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        console.log('🔍 Fetching case study with ID:', id);
        const response = await getMsqCaseStudyById(id);
        
        if (response.success && response.data) {
          console.log('✅ Case study fetched successfully:', response.data.data);
          setCaseStudy(response.data.data.case_study);
        } else {
          console.warn('⚠️ API returned no data:', response);
          setError('Case study not found');
        }
      } catch (error) {
        console.error('❌ Error fetching case study:', error);
        setError('Failed to load case study details');
      } finally {
        setLoading(false);
      }
    };

    fetchCaseStudy();
  }, [id]);

  const getStatusBadge = (status) => {
    const statusConfig = msqCaseStudyStatuses.find(s => s.value === status) || msqCaseStudyStatuses[0];
    return (
      <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${statusConfig.color}`}>
        {statusConfig.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const extractVideoUrl = (videoContent) => {
    if (!videoContent) return null;
    
    // Extract URL from HTML content
    const urlMatch = videoContent.match(/href="([^"]+)"/);
    return urlMatch ? urlMatch[1] : null;
  };

  const extractVideoTitle = (videoContent) => {
    if (!videoContent) return 'Case Study Video';
    
    // Extract text content from HTML
    const textContent = videoContent.replace(/<[^>]*>/g, '');
    return textContent.trim() || 'Case Study Video';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="h-16 w-16 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-600 mb-2">Error Loading Case Study</h2>
        <p className="text-gray-500 mb-4">{error}</p>
        <div className="space-x-4">
          <button
            onClick={() => navigate('/admin/msq-case-studies')}
            className="btn-primary"
          >
            View All Case Studies
          </button>
          <button
            onClick={() => window.location.reload()}
            className="btn-secondary"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!caseStudy) {
    return (
      <div className="text-center py-12">
        <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-600 mb-2">No Case Study Found</h2>
        <p className="text-gray-500 mb-4">The requested case study could not be found.</p>
        <button
          onClick={() => navigate('/admin/msq-case-studies')}
          className="btn-primary"
        >
          View All Case Studies
        </button>
      </div>
    );
  }

  const getAttachmentUrl = (metaItem) => {
    if (!metaItem || typeof metaItem !== 'object') return null;
    return (
      metaItem.pdf_attach ||
      metaItem.meta_attach ||
      metaItem.attachment ||
      metaItem.file ||
      null
    );
  };

  const getMetaItems = () => {
    if (!caseStudy.meta) return [];
    if (Array.isArray(caseStudy.meta)) return caseStudy.meta;

    if (typeof caseStudy.meta === 'string') {
      try {
        const parsed = JSON.parse(caseStudy.meta);
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        return [];
      }
    }

    if (typeof caseStudy.meta === 'object') {
      if (Array.isArray(caseStudy.meta.attachments)) {
        return caseStudy.meta.attachments;
      }
      if (Array.isArray(caseStudy.meta.meta)) {
        return caseStudy.meta.meta;
      }
    }

    return [];
  };

  const metaItems = getMetaItems();

  return (
    <div className="max-w-1xl mx-auto space-y-6">
      {/* Header
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/admin/msq-case-studies')}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Back to Case Studies</span>
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Case Study Details</h1>
            <p className="text-gray-600">View case study information and implementation details</p>
          </div>
        </div>
        {getStatusBadge(caseStudy.status)}
      </div>
      */}
      {/* Main Content Card */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-primary-500 to-primary-600 px-6 py-8 text-white">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-4">
              <div className="bg-white bg-opacity-20 rounded-full p-3">
                <FileText className="h-8 w-8" />
              </div>
              <div>
                <div 
                  className="text-1xl font-bold"
                  dangerouslySetInnerHTML={{ __html: caseStudy.title }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="p-6 space-y-8">
            {/* Description */}
            <div>
             {/*<h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                Description
             </h3>*/}
             
              <div 
                className="prose max-w-none text-gray-700"
                dangerouslySetInnerHTML={{ __html: caseStudy.description }}
              />
             
            </div>

          {/* Training Video */}
          {caseStudy.video_url && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <ExternalLink className="h-5 w-5 text-blue-500 mr-2" />
                Case Study Resources
              </h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <div 
                  className="prose max-w-none text-gray-700 mb-4"
                  dangerouslySetInnerHTML={{ __html: caseStudy.video_url }}
                />
                {extractVideoUrl(caseStudy.video_url) && (
                  <a
                    href={extractVideoUrl(caseStudy.video_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <ExternalLink className="h-4 w-4" />
                    <span>Watch Case Study Video</span>
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Meta Attachments */}
          {metaItems.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                Attachments
              </h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {metaItems.map((meta, index) => {
                    const attachmentUrl = getAttachmentUrl(meta);
                    const attachmentTitle = meta.pdf_name || meta.meta_key || `Attachment ${index + 1}`;

                    return (
                      <div key={index} className="bg-white rounded-lg p-4 border border-gray-200 space-y-3">
                        <div className="flex items-center space-x-2">
                          <FileText className="h-5 w-5 text-primary-500" />
                          <h4 className="text-lg font-semibold text-gray-800">{attachmentTitle}</h4>
                        </div>
                        <div className="space-y-2">
                          {(meta.pdf_description || meta.meta_value) && (
                            <div
                              className="text-sm text-gray-600"
                              dangerouslySetInnerHTML={{ __html: meta.pdf_description || meta.meta_value }}
                            />
                          )}
                          {(meta.meta_button_label || attachmentTitle || attachmentUrl) && (
                            <div className="pt-2">
                              <a
                                href={attachmentUrl || meta.meta_link || '#'}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`inline-flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                                  attachmentUrl || meta.meta_link
                                    ? 'accreditation-attachment-btn text-white'
                                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                }`}
                                aria-disabled={!(attachmentUrl || meta.meta_link)}
                                onClick={(e) => {
                                  if (!(attachmentUrl || meta.meta_link)) {
                                    e.preventDefault();
                                  }
                                }}
                              >
                                <span>Open Attachment</span>
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Timeline 
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Calendar className="h-5 w-5 text-purple-500 mr-2" />
              Timeline
            </h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Created</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {formatDate(caseStudy.created_at)}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Last Updated</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {formatDate(caseStudy.updated_at)}
                  </p>
                </div>
              </div>
            </div>
          </div>
            */}

          {/* Additional Information 
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-md font-semibold text-blue-900 mb-2">Important Notes</h4>
            <ul className="text-blue-800 space-y-1">
              <li>• This case study demonstrates real-world implementation</li>
              <li>• Results and metrics are based on actual project outcomes</li>
              <li>• Contact your administrator for any questions about this case study</li>
            </ul>
          </div>
          */}
        </div>
      </div>

      {/* Action Buttons 
      <div className="flex justify-center space-x-4">
        <button
          onClick={() => navigate('/admin/msq-case-studies')}
          className="btn-secondary flex items-center space-x-2"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back to Case Studies</span>
        </button>
        {extractVideoUrl(caseStudy.video_url) && (
          <a
            href={extractVideoUrl(caseStudy.video_url)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary flex items-center space-x-2"
          >
            <ExternalLink className="h-4 w-4" />
            <span>View Resources</span>
          </a>
        )}
      </div>
      */}
    </div>
  );
};

export default CaseStudy;

